import { useCallback, useEffect, useId, useRef, useState, type RefObject } from "react";
import type { ReactNode } from "react";
import { useColumnFilter } from "@/hooks/use-column-filter";
import { ColumnFilterPopover, type SortDirection } from "@/components/base/column-filter-popover";
import { ColumnHeader } from "@/components/base/column-header";
import { Button, Input, Select, Toggle } from "@its-engineering-and-development/ui-foundation/components";
import type { TablePageInfo } from "@its-engineering-and-development/ui-foundation/components";
import {
    useNonExpenseUsersSearch,
    useUpdateUser,
    useUserDetail,
    useUserManager,
    useUsers,
    type NonExpenseUserSearchResult,
    type UsersQueryFilters,
} from "@app/hooks/use-users";
import { useGroups } from "@app/hooks/use-groups";
import { Drawer } from "@app/components/base/drawer/drawer";
import { DataTableShell } from "@app/components/base/data-table/data-table";
import { Spinner } from "@app/components/base/spinner/spinner";
import { ACTIONS } from "@/auth";
import { useCan } from "@/hooks/use-can";
import type { CreditCard, NameDetails, User, UserGroup, UserStatus, Approver } from "@app/types/users";
import type { GroupRef } from "@app/types/permissions";
import { USER_SORT_FIELD, USER_FILTER_FIELD } from "@app/constants/user-sort-fields";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function legalFullName(user: { legalName: NameDetails | null }): string | null {
    const { firstName, lastName } = user.legalName ?? {};
    if (firstName || lastName) return [firstName, lastName].filter(Boolean).join(" ");
    return null;
}

// A manager with an id but no resolvable legal name means the linked user record wasn't found.
function managerEmailValue(manager: Approver | null): string {
    if (!manager) return "No Manager Assigned";
    if (!legalFullName(manager)) return "Unknown User";
    return manager.emailId ?? "";
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function UserStatusBadge({ status }: { status: string | null }) {
    if (!status) return <span className="text-gray-400">—</span>;

    const map: Record<string, string> = {
        Active: "bg-green-100 text-green-700",
        Pending: "bg-orange-100 text-orange-600",
        Inactive: "bg-gray-100 text-gray-500",
    };

    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${map[status] ?? "bg-gray-100 text-gray-700"}`}>{status}</span>
    );
}

// ─── Group Cell ───────────────────────────────────────────────────────────────

function GroupCell({ group }: { group: UserGroup | null }) {
    if (!group) return <span className="text-gray-400">—</span>;
    if (!group.groupName) {
        return (
            <span className="flex items-center gap-1 text-amber-600">
                <IconWarning />
                Unknown Group
            </span>
        );
    }
    return <span>{group.groupName}</span>;
}

// ─── Approvers Cell ───────────────────────────────────────────────────────────

function approverDisplayName(approver: Approver): string | null {
    return legalFullName(approver);
}

function ApproversCell({ approvers }: { approvers: Approver[] }) {
    // An empty list means the group's approver mode is "None" — no approvers apply.
    if (approvers.length === 0) return <span className="text-gray-400">None</span>;

    const names = approvers.map((approver) => approverDisplayName(approver) ?? "Unknown").join(", ");

    return (
        <span className="block max-w-[220px] truncate" title={names}>
            {names}
        </span>
    );
}

// ─── Credit Cards Cell ────────────────────────────────────────────────────────

function CreditCardsCell({ cards }: { cards: CreditCard[] }) {
    if (cards.length === 0) return <span className="text-gray-400">—</span>;

    return (
        <div className="flex flex-col gap-1">
            {cards.map((card) => (
                <span key={card.creditCardKey} className="flex items-center gap-1 text-sm">
                    <span>*{card.last4}</span>
                    {card.isDuplicate && (
                        <span title="Duplicate card">
                            <IconWarning />
                        </span>
                    )}
                    {card.hasNoCharges && <span className="rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-500">No charges</span>}
                </span>
            ))}
        </div>
    );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconWarning() {
    return (
        <svg className="inline-block h-3.5 w-3.5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
    );
}

function IconDownload() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 3v12m0 0-4-4m4 4 4-4M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
        </svg>
    );
}

function IconUserPlus() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm-6 14a6 6 0 0 1 12 0" />
            <path strokeLinecap="round" d="M19 8v6m-3-3h6" />
        </svg>
    );
}

// ─── Column filter options ────────────────────────────────────────────────────

const USER_STATUS_FILTER_OPTIONS = [
    { label: "Active", value: "Active" },
    { label: "Pending", value: "Pending" },
    { label: "Inactive", value: "Inactive" },
];

// ─── Users Table ──────────────────────────────────────────────────────────────

type UserColumnKey = "username" | "email" | "status" | "group" | "approver" | "last4";

const USERS_SEARCH_HISTORY_KEY = "expense-admin:users-column-search-history";

const SORT_BY_MAP: Partial<Record<UserColumnKey, string>> = {
    username: USER_SORT_FIELD.Name,
    email: USER_SORT_FIELD.Email,
    status: USER_SORT_FIELD.Status,
};

const FILTERABLE_COLUMNS = new Set<UserColumnKey>(["username", "email", "status"]);

function isValidEmailInput(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function UsersTable({
    users,
    isLoading,
    pageInfo,
    onPageChange,
    onSelect,
    activeFilters,
    sort,
    onFilterApply,
    onFilterClear,
    onSortChange,
}: {
    users: User[];
    isLoading: boolean;
    pageInfo: TablePageInfo | null;
    onPageChange: (page: number) => void;
    onSelect: (userId: string) => void;
    activeFilters: Partial<Record<UserColumnKey, string>>;
    sort: { by: string; order: SortDirection } | null;
    onFilterApply: (column: UserColumnKey, value: string) => void;
    onFilterClear: (column: UserColumnKey) => void;
    onSortChange: (sortByValue: string) => void;
}) {
    const { activeFilterColumn, activeFilterAnchor, filterInput, searchHistory, setFilterInput, openColumnFilter, closeFilter, removeSaved, applyFilter } =
        useColumnFilter<UserColumnKey>(USERS_SEARCH_HISTORY_KEY);

    const colHeader = (key: UserColumnKey, label: string, opts?: { options?: Array<{ label: string; value: string }>; hint?: string }) => {
        const sortByValue = SORT_BY_MAP[key];
        const isFilterable = FILTERABLE_COLUMNS.has(key);
        const isSortable = Boolean(sortByValue);
        const isCurrentSort = Boolean(sortByValue && sort?.by === sortByValue);
        const currentDirection = isCurrentSort ? sort?.order : undefined;

        const isEmailColumn = key === "email";
        const inputIsReady = isEmailColumn ? isValidEmailInput(filterInput.trim()) : Boolean(filterInput.trim());

        const handleApply =
            isFilterable && inputIsReady
                ? () => {
                      applyFilter(filterInput, !opts?.options);
                      onFilterApply(key, filterInput.trim());
                  }
                : undefined;

        const handleClear =
            isFilterable && activeFilters[key]
                ? () => {
                      onFilterClear(key);
                      closeFilter();
                  }
                : undefined;

        return [
            <ColumnHeader
                key={`${key}-header`}
                label={label}
                isFilterable={isFilterable}
                isFilterActive={Boolean(activeFilters[key])}
                isFilterOpen={activeFilterColumn === key}
                onFilterPress={(anchorEl) => openColumnFilter(key, anchorEl, activeFilters[key] ?? "")}
                isSortable={isSortable}
                showSortControl
                isSortActive={isCurrentSort}
                sortDirection={currentDirection}
                onSort={() => onSortChange(sortByValue!)}
            />,
            isFilterable ? (
                <ColumnFilterPopover
                    key={`${key}-filter`}
                    isOpen={activeFilterColumn === key}
                    anchorEl={activeFilterAnchor}
                    columnLabel={label}
                    hint={opts?.hint}
                    options={opts?.options}
                    searchValue={filterInput}
                    savedValues={opts?.options ? undefined : (searchHistory[key] ?? [])}
                    onSearchChange={setFilterInput}
                    onClose={closeFilter}
                    onApply={handleApply}
                    onClear={handleClear}
                    onRemoveSavedValue={removeSaved}
                    onSelectValue={(value) => {
                        applyFilter(value, false);
                        onFilterApply(key, value);
                    }}
                />
            ) : null,
        ];
    };

    return (
        <DataTableShell
            tabName="users"
            isLoading={isLoading}
            loadingDescription="Please wait while the users load."
            minWidth="900px"
            pageInfo={pageInfo}
            onPageChange={onPageChange}
            isEmpty={users.length === 0}
            hasActiveFilters={Object.keys(activeFilters).length > 0}
            emptyMessage={Object.keys(activeFilters).length > 0 ? "No users match the current filters." : "No users found."}
            header={
                <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                    <tr>
                        {colHeader("username", "Username")}
                        {colHeader("email", "Email", { hint: "Enter a full email address (e.g. user@example.com)" })}
                        {colHeader("status", "Status", { options: USER_STATUS_FILTER_OPTIONS })}
                        {colHeader("group", "Group")}
                        {colHeader("approver", "Approver")}
                        {colHeader("last4", "Last 4")}
                    </tr>
                </thead>
            }
        >
            <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                    <UserRow key={user.userId} user={user} onSelect={onSelect} />
                ))}
            </tbody>
        </DataTableShell>
    );
}

// ─── User Detail Panel ───────────────────────────────────────────────────────

function SectionTitle({ children }: { children: ReactNode }) {
    return <p className="mb-3 text-lg font-semibold text-gray-900">{children}</p>;
}

const SUFFIX_OPTIONS = ["None", "Jr.", "Sr.", "II", "III", "IV"] as const;

const US_STATES = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
] as const;

const COUNTRIES = ["United States", "Canada", "United Kingdom", "Australia", "Mexico"] as const;

function NameFields({ name, readonly }: { name: NameDetails | null; readonly: boolean }) {
    const suffixValue = name?.suffix && SUFFIX_OPTIONS.includes(name.suffix as (typeof SUFFIX_OPTIONS)[number]) ? name.suffix : "None";

    return (
        <div className="grid grid-cols-2 gap-3">
            <Input label="First Name" defaultValue={name?.firstName ?? ""} isDisabled={readonly} />
            <Input label="Middle Name" defaultValue={name?.middleName ?? ""} placeholder="—" isDisabled={readonly} />
            <Input label="Last Name" defaultValue={name?.lastName ?? ""} isDisabled={readonly} />
            <Select label="Suffix" defaultSelectedKey={suffixValue} isDisabled={readonly}>
                {SUFFIX_OPTIONS.map((opt) => (
                    <Select.Item key={opt} id={opt}>
                        {opt}
                    </Select.Item>
                ))}
            </Select>
        </div>
    );
}

function UserDetailContent({
    user,
    groups,
    selectedGroupId,
    onGroupChange,
}: {
    user: User;
    groups: GroupRef[];
    selectedGroupId: string;
    onGroupChange: (groupId: string) => void;
}) {
    const [nameTab, setNameTab] = useState<"preferred" | "legal">("preferred");

    return (
        <div className="px-5 py-5">
            {/* Account Status */}
            <div className="mb-5 flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">Account Status</span>
                <div className="flex items-center gap-2">
                    <Toggle
                        label={user.status === "Active" ? "Active" : "Inactive"}
                        isSelected={user.status === "Active"}
                        onChange={() => {}}
                        size="md"
                        isDisabled
                    />
                </div>
            </div>

            {/* Company Email */}
            <div className="mb-6">
                <Input label="Company Email" value={user.emailId ?? ""} isDisabled />
            </div>

            {/* Name */}
            <SectionTitle>Name</SectionTitle>
            {/* Preferred / Legal toggle */}
            <div className="mb-4 flex rounded-lg border border-gray-200 bg-gray-100 p-0.5">
                {(["preferred", "legal"] as const).map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        onClick={() => setNameTab(tab)}
                        className={`flex-1 rounded-md py-1.5 text-sm font-medium transition ${
                            nameTab === tab ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {tab === "preferred" ? "Preferred Name" : "Legal Name"}
                    </button>
                ))}
            </div>
            <div className="mb-6">
                <NameFields key={nameTab} name={nameTab === "preferred" ? user.preferredName : user.legalName} readonly />
            </div>

            {/* Address — fields not returned by API, shown as placeholders */}
            <SectionTitle>Address</SectionTitle>
            <div className="mb-6 flex flex-col gap-3">
                <Input label="City" defaultValue="" placeholder="—" isDisabled />
                <div className="grid grid-cols-2 gap-3">
                    <Select label="State" placeholder="—" isDisabled>
                        {US_STATES.map((s) => (
                            <Select.Item key={s} id={s}>
                                {s}
                            </Select.Item>
                        ))}
                    </Select>
                    <Input label="ZIP" defaultValue="" placeholder="—" isDisabled />
                </div>
                <Select label="Country" placeholder="—" isDisabled>
                    {COUNTRIES.map((c) => (
                        <Select.Item key={c} id={c}>
                            {c}
                        </Select.Item>
                    ))}
                </Select>
            </div>

            {/* Manager Details */}
            <SectionTitle>Manager Details</SectionTitle>
            <p className="mb-3 text-xs text-gray-500">Linking a manager email makes that manager available as an approver in the approval routing chain.</p>
            <div className="mb-6">
                <Input label="Manager Email" value={managerEmailValue(user.manager ?? null)} isDisabled />
            </div>

            {/* Card(s) */}
            <SectionTitle>Card(s)</SectionTitle>
            <div className="mb-6 flex flex-col gap-2">
                {user.creditCards.map((card) => (
                    <div key={card.creditCardKey} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 text-sm">
                        <div>
                            <p className="font-medium text-gray-800">****{card.last4 ?? ""}</p>
                            {card.isDuplicate && <span className="text-[11px] text-amber-600">Duplicate</span>}
                        </div>
                        <button
                            type="button"
                            disabled
                            aria-label="Remove card"
                            className="rounded p-1 text-red-400 opacity-60 cursor-not-allowed"
                            title="Remove not yet supported"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                            </svg>
                        </button>
                    </div>
                ))}
                {user.creditCards.length === 0 && <p className="text-sm text-gray-400">No cards linked.</p>}
                {/* TODO: replace with ui-foundation Input or ComboBox once add card functionality is defined */}
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5">
                    <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <circle cx="11" cy="11" r="8" />
                        <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        disabled
                        placeholder="Type last 4 digits only"
                        className="flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
                        title="Add card not yet supported"
                    />
                </div>
            </div>

            {/* Assign Group */}
            <SectionTitle>Assign Group</SectionTitle>
            <p className="mb-3 text-xs text-gray-500">
                Selecting a group applies that group’s policy rules, approval chain, and budget allocations to the user.
            </p>
            <div className="mb-2">
                <Select label="Group" selectedKey={selectedGroupId} onSelectionChange={(key) => onGroupChange(String(key))} placeholder="— Select a group —">
                    {groups.map((g) => (
                        <Select.Item key={g.groupId} id={g.groupId}>
                            {g.groupName ?? g.groupId}
                        </Select.Item>
                    ))}
                </Select>
            </div>
        </div>
    );
}

// ─── User Search Dropdown (Add User) ─────────────────────────────────────────
// "Add User" drawer search — finds existing users who are not yet Expense users
// so an admin can convert one into an Expense user. Preloads candidates as soon
// as the drawer opens; no minimum-character gate (mirrors MemberSearchDropdown
// in groups-page.tsx, minus the min-chars/infinite-scroll behavior).

function UserSearchDropdown({ onSelect }: { onSelect: (user: NonExpenseUserSearchResult) => void }) {
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputId = useId();
    const listboxId = useId();

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const { users, isLoading } = useNonExpenseUsersSearch(debouncedQuery, isOpen);

    useEffect(() => {
        function handleOutside(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
        }
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8" />
                    <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                </svg>
                <input
                    id={inputId}
                    type="text"
                    value={query}
                    aria-label="Search for a user"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onClick={() => setIsOpen(true)}
                    placeholder="Search for a user..."
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                />
            </div>

            {isOpen && (
                <ul
                    id={listboxId}
                    role="listbox"
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md"
                >
                    {isLoading ? (
                        <li className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-400">
                            <Spinner className="h-4 w-4 text-gray-400" />
                            Loading…
                        </li>
                    ) : users.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-400">No results</li>
                    ) : (
                        users.map((u) => {
                            const name = [u.legalName?.firstName, u.legalName?.lastName].filter(Boolean).join(" ") || u.emailId || u.userId;
                            return (
                                <li key={u.userId} role="presentation">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected="false"
                                        className="w-full px-4 py-3 text-left hover:bg-gray-50"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            onSelect(u);
                                            setQuery("");
                                            setIsOpen(false);
                                        }}
                                    >
                                        <p className="text-sm font-medium text-gray-900">{name}</p>
                                        {u.emailId && <p className="text-xs text-gray-500">{u.emailId}</p>}
                                    </button>
                                </li>
                            );
                        })
                    )}
                </ul>
            )}
        </div>
    );
}

// Add User drawer must look identical to Update User once a person is selected — build a
// full User shape from the (lightweight) search result, with placeholders for fields the
// search doesn't return (mirrors what UserDetailContent already shows for a real user whose
// address/manager/cards are unavailable).
const VALID_USER_STATUSES: readonly UserStatus[] = ["Active", "Pending", "Inactive"];

// The users service can return statuses outside our known union — fall back to null rather than trusting the cast.
function toUserStatus(status: string | null): UserStatus | null {
    return status && (VALID_USER_STATUSES as readonly string[]).includes(status) ? (status as UserStatus) : null;
}

function userFromSearchResult(searchResult: NonExpenseUserSearchResult, manager: Approver | null): User {
    const toNameDetails = (name: { firstName: string | null; lastName: string | null } | null): NameDetails | null =>
        name ? { firstName: name.firstName, middleName: null, lastName: name.lastName, suffix: null } : null;

    return {
        userId: searchResult.userId,
        legalName: toNameDetails(searchResult.legalName),
        preferredName: toNameDetails(searchResult.preferredName),
        emailId: searchResult.emailId,
        status: toUserStatus(searchResult.status),
        isTraveler: null,
        isExpenseUser: false,
        companyId: searchResult.companyId,
        managerRoleId: null,
        legacyUserKey: null,
        expenseProfileId: null,
        createdAt: null,
        updatedAt: null,
        group: null,
        managerUserId: null,
        approvers: [],
        manager,
        updatedBy: null,
        lastActivity: null,
        creditCards: [],
    };
}

function UserDetailPanel({
    userId,
    mode,
    onClose,
    triggerRef,
    onUpdated,
    canWrite,
}: {
    userId: string | null;
    mode: "create" | "update";
    onClose: () => void;
    triggerRef: RefObject<HTMLElement | null>;
    onUpdated: () => void;
    canWrite: boolean;
}) {
    const isCreateMode = mode === "create";
    // Create mode never looks up an existing user — it only searches for non-Expense users to convert.
    const { user, isLoading, error } = useUserDetail(isCreateMode ? null : userId);
    const [selectedNewUser, setSelectedNewUser] = useState<NonExpenseUserSearchResult | null>(null);
    // Search results don't include manager — fetch it separately once a user is selected.
    const { manager: selectedNewUserManager } = useUserManager(isCreateMode ? (selectedNewUser?.userId ?? null) : null);
    const { groups } = useGroups();
    const { updateUser, isLoading: isUpdating, error: updateError, clearError } = useUpdateUser();
    const [selectedGroupId, setSelectedGroupId] = useState<string>("");
    const [baselineGroupId, setBaselineGroupId] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Sync group selection when user data loads (update mode only)
    useEffect(() => {
        if (user?.group?.groupId) {
            setSelectedGroupId(user.group.groupId);
            setBaselineGroupId(user.group.groupId);
        }
    }, [user]);

    const hasChanges = user ? selectedGroupId !== baselineGroupId : false;
    const canSaveNewUser = Boolean(selectedNewUser && selectedGroupId);

    // Return focus to the triggering row/button on close
    const handleClose = useCallback(() => {
        onClose();
        triggerRef.current?.focus();
    }, [onClose, triggerRef]);

    const handleUpdateUser = useCallback(async () => {
        if (!user || !hasChanges || !user.companyId || !user.status) return;
        clearError();
        setSuccessMessage(null);
        try {
            await updateUser(user.userId, {
                companyId: user.companyId,
                groupId: selectedGroupId,
                status: user.status,
            });
            setBaselineGroupId(selectedGroupId);
            setSuccessMessage("User group updated successfully");
            onUpdated();
        } catch {
            // error is already set by the hook
        }
    }, [user, hasChanges, selectedGroupId, updateUser, clearError, onUpdated]);

    const handleAddUser = useCallback(async () => {
        if (!selectedNewUser || !selectedGroupId) return;
        clearError();
        setSuccessMessage(null);
        try {
            // Status is intentionally omitted — converting a user to an Expense user doesn't change their account status.
            await updateUser(selectedNewUser.userId, {
                companyId: selectedNewUser.companyId,
                groupId: selectedGroupId,
                isExpenseUser: true,
            });
            onUpdated();
            handleClose();
        } catch {
            // error is already set by the hook
        }
    }, [selectedNewUser, selectedGroupId, updateUser, clearError, onUpdated, handleClose]);

    return (
        <Drawer
            title={isCreateMode ? "Add User" : "Update User"}
            onClose={handleClose}
            maxWidth="520px"
            isLoading={(!isCreateMode && isLoading) || isUpdating}
            loadingLabel={isCreateMode ? "Adding user" : "Loading user"}
            loadingMessage={isUpdating ? (isCreateMode ? "Adding user…" : "Updating user…") : "Loading user details…"}
            bodyClassName="p-0"
            footer={
                <>
                    {updateError && <p className="mb-2 text-center text-sm text-red-600">{updateError}</p>}
                    {successMessage && <p className="mb-2 text-center text-sm text-green-600">{successMessage}</p>}
                    {isCreateMode ? (
                        <Button color="primary" size="md" isDisabled={!canWrite || !canSaveNewUser || isUpdating} className="w-full" onPress={handleAddUser}>
                            Add User
                        </Button>
                    ) : (
                        <Button color="primary" size="md" isDisabled={!canWrite || !hasChanges || isUpdating} className="w-full" onPress={handleUpdateUser}>
                            Update User
                        </Button>
                    )}
                </>
            }
        >
            {isCreateMode ? (
                <>
                    <div className="px-5 pt-5">
                        <UserSearchDropdown onSelect={setSelectedNewUser} />
                        {!selectedNewUser && <p className="mt-3 text-sm text-gray-400">Search for a user above to get started.</p>}
                    </div>
                    {selectedNewUser && (
                        <UserDetailContent
                            user={userFromSearchResult(selectedNewUser, selectedNewUserManager)}
                            groups={groups}
                            selectedGroupId={selectedGroupId}
                            onGroupChange={setSelectedGroupId}
                        />
                    )}
                </>
            ) : (
                <>
                    {error && !isLoading && (
                        <div className="flex h-full items-center justify-center px-6">
                            <p className="text-center text-sm text-red-600">{error}</p>
                        </div>
                    )}
                    {user && !isLoading && (
                        <UserDetailContent user={user} groups={groups} selectedGroupId={selectedGroupId} onGroupChange={setSelectedGroupId} />
                    )}
                </>
            )}
        </Drawer>
    );
}

// ─── User Row ─────────────────────────────────────────────────────────────────

function UserRow({ user, onSelect }: { user: User; onSelect: (userId: string) => void }) {
    const displayName = legalFullName(user) ?? user.emailId ?? user.userId;

    return (
        <tr
            className="cursor-pointer hover:bg-gray-50"
            tabIndex={0}
            data-user-id={user.userId}
            onClick={() => onSelect(user.userId)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(user.userId);
                }
            }}
        >
            {/* Username */}
            <td className="px-4 py-3 font-medium text-blue-600">
                <button
                    type="button"
                    className=""
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelect(user.userId);
                    }}
                >
                    {displayName}
                </button>
            </td>

            {/* Email */}
            <td className="px-4 py-3 text-gray-700">{user.emailId ? <span>{user.emailId}</span> : <span className="text-gray-400">—</span>}</td>

            {/* Status */}
            <td className="px-4 py-3">
                <UserStatusBadge status={user.status} />
            </td>

            {/* Group */}
            <td className="px-4 py-3 text-gray-700">
                <GroupCell group={user.group} />
            </td>

            {/* Approver(s) */}
            <td className="px-4 py-3 text-gray-700">
                <ApproversCell approvers={user.approvers ?? []} />
            </td>

            {/* Last 4 (credit cards) */}
            <td className="px-4 py-3 text-gray-700">
                <CreditCardsCell cards={user.creditCards} />
            </td>
        </tr>
    );
}

// ─── Users Page ───────────────────────────────────────────────────────────────

export function UsersPage() {
    const canWrite = useCan(ACTIONS.users.write);
    const [activeFilters, setActiveFilters] = useState<Partial<Record<UserColumnKey, string>>>({});
    const [sort, setSort] = useState<{ by: string; order: SortDirection } | null>(null);

    const usersFilters: UsersQueryFilters = {
        [USER_FILTER_FIELD.Name]: activeFilters.username,
        [USER_FILTER_FIELD.Email]: activeFilters.email,
        [USER_FILTER_FIELD.Status]: activeFilters.status,
        isExpenseUser: true,
        sortBy: sort?.by,
        sortOrder: sort?.order,
    };

    const { users, pageInfo, currentPage, isLoading, error, setPage, refresh } = useUsers(usersFilters);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [isAddingUser, setIsAddingUser] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);

    const tablePageInfo: TablePageInfo | null = pageInfo
        ? {
              totalCount: pageInfo.totalCount,
              offset: currentPage * pageInfo.limit,
              limit: pageInfo.limit,
              hasNextPage: pageInfo.hasNextPage,
          }
        : null;

    return (
        <div className="flex flex-col gap-6">
            {/* Page heading + actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
                    <p className="mt-2 text-sm text-gray-600">Manage Expense user access, group assignment, and credit card association.</p>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                    <Button color="secondary" size="md" isDisabled title="Export not yet supported" iconLeading={IconDownload}>
                        Export
                    </Button>
                    <Button
                        color="primary"
                        size="md"
                        iconLeading={IconUserPlus}
                        onPress={(e) => {
                            triggerRef.current = (e.target as HTMLElement) ?? null;
                            setIsAddingUser(true);
                        }}
                    >
                        Add User
                    </Button>
                </div>
            </div>

            {/* Error */}
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            {/* Table */}
            <UsersTable
                users={users}
                isLoading={isLoading}
                pageInfo={tablePageInfo}
                onPageChange={setPage}
                onSelect={(userId) => {
                    triggerRef.current = document.querySelector<HTMLElement>(`[data-user-id="${userId}"]`);
                    setSelectedUserId(userId);
                }}
                activeFilters={activeFilters}
                sort={sort}
                onFilterApply={(column, value) => setActiveFilters((prev) => ({ ...prev, [column]: value }))}
                onFilterClear={(column) =>
                    setActiveFilters((prev) => {
                        const next = { ...prev };
                        delete next[column];
                        return next;
                    })
                }
                onSortChange={(sortByValue) =>
                    setSort((prev) => {
                        if (!prev || prev.by !== sortByValue) return { by: sortByValue, order: "asc" };
                        if (prev.order === "asc") return { by: sortByValue, order: "desc" };
                        return null;
                    })
                }
            />

            {/* User detail / Add User drawer */}
            {(selectedUserId || isAddingUser) && (
                <UserDetailPanel
                    userId={selectedUserId}
                    mode={isAddingUser ? "create" : "update"}
                    onClose={() => {
                        setSelectedUserId(null);
                        setIsAddingUser(false);
                    }}
                    triggerRef={triggerRef}
                    onUpdated={refresh}
                    canWrite={canWrite}
                />
            )}
        </div>
    );
}
